import API_CONFIG from "../config/api.js";

export type DeliveryStatus = "pending" | "delivered";

export interface EligibleSaleForDelivery {
  id: number;
  company_id: number;
  warehouse_id: number;
  user_id: number | null;
  invoice_number: string;
  order_date: string;
  total: number;
  payment_status: string;
  order_status: string;
  warehouse_name: string | null;
  client_name: string | null;
}

export interface SalesDelivery {
  id: number;
  company_id: number;
  warehouse_id: number;
  order_id: number;
  delivery_number: string;
  delivery_date: string;
  status: string;
  recipient_name: string;
  recipient_phone?: string | null;
  notes?: string | null;
  invoice_number?: string | null;
  client_name?: string | null;
  warehouse_name?: string | null;
  payment_status?: string | null;
  order_status?: string | null;
}

export interface SalesDeliveryItem {
  id: number;
  delivery_id: number;
  order_item_id: number | null;
  product_id: number;
  product_name_snapshot: string;
  unit_name_snapshot?: string | null;
  unit_short_name_snapshot?: string | null;
  quantity: number;
}

export interface SalesDeliveryDetail extends SalesDelivery {
  client_phone?: string | null;
  client_email?: string | null;
  client_address?: string | null;
  client_name_snapshot?: string | null;
  warehouse_address?: string | null;
  warehouse_phone?: string | null;
  warehouse_email?: string | null;
  delivered_by_name?: string | null;
  delivered_by_name_snapshot?: string | null;
  invoice_number_snapshot?: string | null;
  delivery_status?: DeliveryStatus;
  items: SalesDeliveryItem[];
}

export interface SalesDeliveryFilters {
  company_id?: number | null;
  warehouse_id?: number | null;
  user_id?: number | null;
  invoice_number?: string;
  delivery_number?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface DeliveryCreatePayload {
  order_id: number;
  delivery_date: string;
  recipient_name: string;
  recipient_phone?: string;
  notes?: string;
  company_id?: number | null;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const buildQueryString = (params: object) => {
  const searchParams = new URLSearchParams();

  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    searchParams.set(key, String(value));
  });

  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : "";
};

const readErrorMessage = async (response: Response, fallbackMessage: string) => {
  try {
    const rawText = await response.text();
    if (!rawText) {
      return fallbackMessage;
    }

    try {
      const parsed = JSON.parse(rawText);
      return parsed.message || parsed.error || parsed.details || fallbackMessage;
    } catch (error) {
      return rawText || fallbackMessage;
    }
  } catch (error) {
    return fallbackMessage;
  }
};

export async function fetchEligibleSalesForDelivery(
  filters: SalesDeliveryFilters = {}
): Promise<{ eligibleSales: EligibleSaleForDelivery[]; pagination: { page: number; limit: number; total: number } }> {
  const response = await fetch(
    `${API_CONFIG.API_URL}/sales-deliveries/eligible-sales${buildQueryString(filters)}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        "Erreur lors de la récupération des ventes à livrer."
      )
    );
  }

  return response.json();
}

export async function fetchSalesDeliveries(
  filters: SalesDeliveryFilters = {}
): Promise<{ deliveries: SalesDelivery[]; pagination: { page: number; limit: number; total: number } }> {
  const response = await fetch(
    `${API_CONFIG.API_URL}/sales-deliveries${buildQueryString(filters)}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        "Erreur lors de la récupération des bordereaux."
      )
    );
  }

  return response.json();
}

export async function fetchSalesDeliveryDetail(
  deliveryId: number,
  filters: Pick<SalesDeliveryFilters, "company_id"> = {}
): Promise<SalesDeliveryDetail> {
  const response = await fetch(
    `${API_CONFIG.API_URL}/sales-deliveries/${deliveryId}${buildQueryString(filters)}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        "Erreur lors de la récupération du bordereau."
      )
    );
  }

  return response.json();
}

export async function createSalesDelivery(
  payload: DeliveryCreatePayload
): Promise<{ message: string; delivery: SalesDeliveryDetail }> {
  const response = await fetch(`${API_CONFIG.API_URL}/sales-deliveries`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        "Erreur lors de l'émission du bordereau."
      )
    );
  }

  return response.json();
}

export async function downloadSalesDeliveryPdf(
  deliveryId: number,
  fallbackName = "bordereau_livraison.pdf"
) {
  const response = await fetch(
    `${API_CONFIG.API_URL}/sales-deliveries/${deliveryId}/pdf`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        "Erreur lors du téléchargement du bordereau."
      )
    );
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.href = blobUrl;
  downloadLink.download = fallbackName;
  downloadLink.style.display = "none";
  document.body.appendChild(downloadLink);
  downloadLink.click();

  setTimeout(() => {
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(blobUrl);
  }, 100);
}
